import { Bell, FileSpreadsheet, SettingsIcon, CreditCard } from "lucide-react";
import { AiOutlinePound } from "react-icons/ai";
import { TbReplace } from "react-icons/tb";
import { TbTextPlus } from "react-icons/tb";
import { MdOutlineWrapText, MdOutlineFastfood, MdOutlineAccessTime, MdShare, MdClose } from "react-icons/md";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback, useRef} from "react";
import useAuth from "@/hooks/useAuth";
import {
  getNotificationsApi,
  updateNotificationsReadStatusApi,
} from "@/api/notifications";
import PusherHandler from "@/api/lib/pusher/PusherHandler";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { markAsPaid } from "@/redux/slice/take-order-slice/order-slice";
import { notificationSound } from "@/assets";
import { getUserSettingsApi } from "@/api/settings/take-order-settings";
import useSettings from "@/hooks/useSettings";
import { getReservationById } from "@/api/new-api/reservations/reservation";
import { updateReservationState } from "@/redux/slice/reservation-slice/reservation-slice";
const skeletonRows = Array.from({ length: 5 });

const getLondonTimeParts = (dateInput) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(dateInput);
  const year = parts.find((p) => p.type === "year").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  const hour = parts.find((p) => p.type === "hour").value;
  const minute = parts.find((p) => p.type === "minute").value;
  const second = parts.find((p) => p.type === "second").value;
  return new Date(year, month - 1, day, hour, minute, second);
};

const parseAsLondonTime = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return getLondonTimeParts(dateValue);
  }
  const str = String(dateValue).trim();
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(str);
  if (hasTimezone) {
    const parsedDate = new Date(str);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return getLondonTimeParts(parsedDate);
  } else {
    const normalizedStr = str.replace("T", " ");
    const match = normalizedStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [_, year, month, day, hour, minute, second] = match;
      return new Date(year, month - 1, day, hour, minute, second);
    } else {
      const parsedDate = new Date(str);
      if (Number.isNaN(parsedDate.getTime())) return null;
      return parsedDate;
    }
  }
};

const formatTimeAgo = (dateValue) => {
  if (!dateValue) return "";
  const eventDate = parseAsLondonTime(dateValue);
  if (!eventDate) return "";

  const nowLondon = getLondonTimeParts(new Date());
  const diff = nowLondon.getTime() - eventDate.getTime();

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
};

function NavNotification({ setShowReloadConfirm }) {
  const { user } = useAuth();
  const restaurant_id = user?.restaurant_id;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const notificationsContainerRef = useRef(null);
  const autoMarkTimeoutRef = useRef(null);
  const { notificationSound: isNotificationSoundEnabled = true, volume } = useSelector(
      (state) => state.utility.soundNotification || {},
  );
  const isNotificationSoundEnabledRef = useRef(isNotificationSoundEnabled);
  const volumeRef = useRef(volume);

  useEffect(() => {
    isNotificationSoundEnabledRef.current = isNotificationSoundEnabled;
  }, [isNotificationSoundEnabled]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const incomingAudioRef = useRef(null);
  const dispatch = useDispatch(); 
  const {settings} = useSettings();



  const stopIncomingSound = useCallback(() => {
      const currentAudio = incomingAudioRef.current;
      if (!currentAudio) return;

      currentAudio.pause();
      currentAudio.currentTime = 0;
      incomingAudioRef.current = null;
  }, []);

  const playIncomingSound = useCallback(() => {
      stopIncomingSound();
 
      console.log("NAV playIncomingSound: isNotificationSoundEnabledRef.current =", isNotificationSoundEnabledRef.current);
      if (!isNotificationSoundEnabledRef.current) return;
 
      const audio = new Audio(notificationSound);
      audio.volume = typeof volumeRef.current === "number" ? volumeRef.current : 1;
      audio.play().catch(() => {});
      incomingAudioRef.current = audio;
  }, [stopIncomingSound]);




  const unreadCount = useMemo(
    () => notifications.filter((item) => !item?.is_read).length,
    [notifications]
  );

  const {
      connectionStatus: tableStatus,
      lastUpdate: lastTableUpdate,
      addEventHandler,
      isConnected: tableConnected
  } = PusherHandler();

  useEffect(() => {
    if (!open || !restaurant_id) return;
    let isActive = true;

    const loadNotifications = async (pageNum = 1) => {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await getNotificationsApi(restaurant_id, {
        per_page: 5,
        page: pageNum,
        category : "notification", 
        till_id : settings?.take_order?.device_id,
      });

      console.log("Notifications response:", response);

      const list =
        response?.data?.notifications || [];
      const totalUnread = response?.data?.unread_count || 0;
      const pagination = response?.data?.pagination || response?.pagination || {};

      if (!isActive) return;

      // Append notifications if loading more, replace if first page
      if (pageNum === 1) {
        setNotifications(Array.isArray(list) ? list : []);
      } else {
        setNotifications((prev) => [...prev, ...list]);
      }
      
      setTotalUnread(totalUnread);
      
      // Check if there are more pages
      const currentPage = pagination.current_page || pagination.currentPage || pageNum;
      const lastPage = pagination.last_page || pagination.lastPage || 1;
      setHasMore(currentPage < lastPage);
      
      if (pageNum === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }

      const unreadIds = (Array.isArray(list) ? list : [])
        .filter((item) => item && item.is_read === false)
        .map((item) => item.id)
        .filter(Boolean);

      if (unreadIds.length && pageNum === 1) {
        const response = await updateNotificationsReadStatusApi(restaurant_id, {
          ids: unreadIds,
          is_read: true,
        });
        // console.log("Update read status response:", response);
        if (response?.status === "success") {
          setTotalUnread(response.data.total_unread || 0 );
        }
        if (!isActive) return;
        setNotifications((prev) =>
          prev.map((item) =>
            unreadIds.includes(item.id) ? { ...item, is_read: true } : item
          )
        );
      }
    };

    setPage(1);
    loadNotifications(1);
    return () => {
      isActive = false;
    };
  }, [open, restaurant_id]);


  useEffect(() => {
    
    addEventHandler('new-notification', (data) => {
      const rawDeviceId = settings?.take_order?.device_id ?? settings?.device_id;
      const currentDeviceId = rawDeviceId != null && rawDeviceId !== "" ? Number(rawDeviceId) : null;
      const hasShared = Array.isArray(data?.shared_till_ids) && data.shared_till_ids.length > 0;
      const hasDisplay = Array.isArray(data?.display_till_ids) && data.display_till_ids.length > 0;

      const matchesShared = hasShared && currentDeviceId != null && data.shared_till_ids.some(id => Number(id) === currentDeviceId);
      const matchesDisplay = hasDisplay && currentDeviceId != null && data.display_till_ids.some(id => Number(id) === currentDeviceId);

      const isForThisTill =
        (!hasShared && !hasDisplay) ||
        currentDeviceId == null ||
        matchesShared ||
        matchesDisplay;

      if (isForThisTill) {
        if(data?.category === "notification") {
          
          if(data?.type === "settings_updated") {
            playIncomingSound();
            if(data?.till_id && data?.till_id !== settings?.take_order?.device_id) {
              getUserSettingsApi().then((res) => {
                if(res?.status === "success") {
                  const newSettings = res.data || {};
                  localStorage.setItem("settings", JSON.stringify(newSettings)?.settings);
                  toast.success(data?.message || "Settings updated");
                }
              });
            }else {
              setNotifications((prev) => [data, ...prev]);
              setTotalUnread((prev) => prev + 1);
              let settings = JSON.parse(localStorage.getItem("settings"));
              if(settings) {
                settings.outdated = true;
                localStorage.setItem("settings", JSON.stringify(settings));
              }

              toast.success(data?.message || "Settings updated");
            }
          }

        }

        if(data?.category === "notification" || data?.category === "online_notification") {
          playIncomingSound();
          if(data?.type === "order_created") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "New order created");
          }
          else if(data?.type === "order_updated") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Order updated");
          }
          else if(data?.type === "order_kitchen_ready") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Order is ready in kitchen");
          }
          else if(data?.type === "order_cooking_time_updated") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Order cooking time updated");
          }
          else if(data?.type === "order_delayed") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Order delayed");
          }
          else if(data?.type === "order_completed") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Order completed");
          }
          else if(data?.type === "order_item_replaced") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "An item in the order has been replaced");
          }
          else if(data?.type === "order_replaced") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "An item in the order has been replaced");
          }
          else if(data?.type === "order_complementary_updated") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "An item in the order has been replaced");
          }
          else if(data?.type === "order_shared_to_tills") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "An item in the order has been shared to tills");
          }
          else if(data?.type === "order_pay_by_link_paid") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || `Payment received for order #${data?.data?.order_id}`);

            dispatch(markAsPaid({
              id: data?.data?.order_id,
              amount: data?.data?.amount,
              method: "pay_by_link"
            }))
          }
          else if(data?.type === "order_completed_unpaid") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || `Order #${data?.data?.order_id} completed but not paid`);

            dispatch(markAsPaid({
              id: data?.data?.order_id,
              amount: 0,
              method: "unpaid"
            }))
          }
          else if(data?.type === "order_payment_received") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || `Payment received for order #${data?.data?.order_id}`);

            dispatch(markAsPaid({
              id: data?.data?.order_id,
              amount: data?.data?.amount,
              method: data?.data?.payment_method,
              pay_amount: data?.data?.pay_amount ?? data?.data?.payment?.pay_amount,
              change_amount: data?.data?.change_amount ?? data?.data?.payment?.change_amount,
              payment: data?.data?.payment,
            }))
          } 
          else if(data?.type === "reservation_deposit_paid") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Reservation deposit paid");

            const reservationId = data?.data?.reservation_id || data?.reservation_id;
            if (reservationId) {
              getReservationById(reservationId)
                .then((res) => {
                  if (res?.status === "success" && res?.data?.reservation) {
                    dispatch(updateReservationState(res.data.reservation));
                  }
                })
                .catch((err) => console.error("Error fetching reservation on notification:", err));
            }
          }
          else if(
            data?.type === "reservation_deposit_payment_cancelled" || 
            data?.type === "reservation_card_payment_cancelled" ||
            data?.type === "reservation_payment_cancelled" ||
            data?.type === "reservation_deposit_payment_failed"
          ) {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.error(data?.message || "Reservation payment cancelled");

            const reservationId = data?.data?.reservation_id || data?.reservation_id;
            if (reservationId) {
              getReservationById(reservationId)
                .then((res) => {
                  if (res?.status === "success" && res?.data?.reservation) {
                    dispatch(updateReservationState(res.data.reservation));
                  }
                })
                .catch((err) => console.error("Error fetching reservation on notification:", err));
            }
          }
          else if(data?.type === "reservation_deposit_refunded") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.error(data?.message || "Reservation deposit refunded");

            const reservationId = data?.data?.reservation_id || data?.reservation_id;
            if (reservationId) {
              getReservationById(reservationId)
                .then((res) => {
                  if (res?.status === "success" && res?.data?.reservation) {
                    dispatch(updateReservationState(res.data.reservation));
                  }
                })
                .catch((err) => console.error("Error fetching reservation on notification:", err));
            }
          }
          else if(data?.type === "reservation_rejected" || data?.type === "reservation_cancelled") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.error(data?.message || "Reservation cancelled");

            const reservationId = data?.data?.reservation_id || data?.reservation_id;
            if (reservationId) {
              getReservationById(reservationId)
                .then((res) => {
                  if (res?.status === "success" && res?.data?.reservation) {
                    dispatch(updateReservationState(res.data.reservation));
                  }
                })
                .catch((err) => console.error("Error fetching reservation on notification:", err));
            }
          }
          else if(data?.type === "c2c_gift_card_payment_received") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "C2C Gift Card Payment Received");
          }
          else if(data?.type === "c2c_gift_card_payment_cancelled" || data?.type === "c2c_gift_card_payment_failed") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.error(data?.message || "C2C Gift Card Payment Cancelled");
          }
          else if(
            data?.type === "course_wise_payment_failed" ||
            data?.type === "course_wise_payment_cancelled"
          ) {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.error(data?.message || "Course-wise card payment cancelled");
          }
          else if(
            data?.type === "course_wise_payment_paid" ||
            data?.type === "course_wise_payment_updated"
          ) {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success(data?.message || "Course-wise card payment received");
          }
          else {
            // General Fallback for any other unhandled notification type
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast(data?.message || data?.title || "New notification received");
          }
        }
      }
    })

  }, [addEventHandler, tableStatus, tableConnected, settings]);

  function isSettingsOutdated(){
    let settings = JSON.parse(localStorage.getItem("settings"));
    if(settings?.outdated) {
      return true;
    }
    return false;
  }

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    setLoadingMore(true);
    
    const response = await getNotificationsApi(restaurant_id, {
      per_page: 5,
      page: nextPage,
      category : "notification", 
    });

    const list = response?.data?.notifications || [];
    const pagination = response?.data?.pagination || response?.pagination || {};

    setNotifications((prev) => [...prev, ...list]);
    
    // Check if there are more pages
    const currentPage = pagination.current_page || pagination.currentPage || nextPage;
    const lastPage = pagination.last_page || pagination.lastPage || 1;
    setHasMore(currentPage < lastPage);
    
    setLoadingMore(false);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!notificationsContainerRef.current) return;
    
    const container = notificationsContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    if (isNearBottom && hasMore && !loadingMore && !loading) {
      handleLoadMore();
    }
  }, [hasMore, loadingMore, loading]);

  useEffect(() => {
    const container = notificationsContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Auto-mark unread notifications as read after 3 seconds
  useEffect(() => {
    if(!open) return;
    // Clear any existing timeout
    if (autoMarkTimeoutRef.current) {
      clearTimeout(autoMarkTimeoutRef.current);
    }

    // Find unread notifications
    const unreadNotifications = notifications.filter((item) => !item?.is_read);

    if (unreadNotifications.length === 0) return;

    // Set timeout to mark them as read after 3 seconds
    autoMarkTimeoutRef.current = setTimeout(async () => {
      const unreadIds = unreadNotifications
        .map((item) => item.id)
        .filter(Boolean);

      if (unreadIds.length > 0) {
        try {
          const response = await updateNotificationsReadStatusApi(restaurant_id, {
            ids: unreadIds,
            is_read: true,
          });

          if (response?.status === "success") {
            setTotalUnread(response.data.total_unread || 0);
            setNotifications((prev) =>
              prev.map((item) =>
                unreadIds.includes(item.id) ? { ...item, is_read: true } : item
              )
            );
          }
        } catch (error) {
          console.error("Error marking notifications as read:", error);
        }
      }
    }, 3000); // 3 seconds

    return () => {
      if (autoMarkTimeoutRef.current) {
        clearTimeout(autoMarkTimeoutRef.current);
      }
    };
  }, [notifications, restaurant_id]);



  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span className="bg-cblack7/60 p-1.5 rounded text-cwhite2 cursor-pointer relative">
          <Bell />
          {(unreadCount > 0 || totalUnread > 0) && (
            <div className="absolute h-2 w-2 bg-color8 rounded-full top-2 right-2"></div>
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent className=" w-[420px] bg-cblack3 -right-8 absolute">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-cwhite">Notification</div>
          <span className="text-sm text-color8">
            { totalUnread ?? unreadCount} Unread
          </span>
        </div>
        <Separator className="mt-3" />
        <div className="space-y-3 text-cwhite2 mt-3 max-h-[500px] overflow-y-auto" ref={notificationsContainerRef}>
          {loading &&
            skeletonRows.map((_, index) => (
              <div
                key={`sk-${index}`}
                className="flex items-center gap-4 rounded-md bg-cblack2/60 p-3 animate-pulse"
              >
                <div className="h-10 w-10 rounded-full bg-cblack7/60" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-4/5 rounded bg-cblack7/60" />
                  <div className="h-3 w-2/5 rounded bg-cblack7/60" />
                </div>
              </div>
            ))}

          {!loading && notifications.length === 0 && (
            <div className="text-sm text-cwhite3 text-center py-6">
              No notifications found.
            </div>
          )}

          {!loading &&
            notifications.map((item) => {
              const message =
                item?.message ||
                item?.title ||
                item?.description ||
                `Notification #${item?.id ?? ""}`;
              const timestamp =
                item?.created_at || item?.createdAt || item?.date;
              const timeAgo = formatTimeAgo(timestamp);

              return (
                <div
                  key={item?.id ?? message}
                  className={`flex justify-between items-center gap-4 text-sm rounded-md p-3 transition
                    ${item?.is_read ? "bg-cblack2/40" : "bg-cblack2"}
                  `}
                >
                  <div className="flex items-center gap-4">
                    <div className={` p-3 rounded-full ${item?.is_read ? "bg-color4" : "bg-cwhite"}`}>
                    {
                      (() => {
                        switch (item?.type) {
                          case "order_created":
                            return <TbTextPlus size={24} className="text-cblack3" />;
                          case "order_updated":
                            return <MdOutlineWrapText size={22} className="text-cblack3" />;
                          case "order_shared_to_tills":
                            return <MdShare size={22} className="text-cblack3" />;
                          case "order_item_replaced":
                          case "order_replaced":
                          case "order_complementary_updated":
                            return <TbReplace size={22} className="text-cblack3" />;
                          case "order_payment_received":
                          case "order_pay_by_link_paid":
                          case "reservation_deposit_paid":
                          case "reservation_deposit_refunded":
                            return <AiOutlinePound size={28} className="text-cblack3" />;
                          case "settings_updated":
                            return <SettingsIcon className="text-cblack3" />;
                          case "order_kitchen_ready":
                          case "order_completed":
                          case "pre_order_ongoing":
                            return <MdOutlineFastfood size={22} className="text-cblack3" />;
                          case "order_cooking_time_updated":
                            return <MdOutlineAccessTime size={26} className="text-cblack3" />;
                          case "order_delayed":
                            return <MdOutlineAccessTime size={22} className="text-cblack3" />;
                          case "order_completed_unpaid":
                            return <AiOutlinePound size={22} className="text-cblack3" />;
                          case "reservation_rejected":
                          case "reservation_cancelled":
                          case "reservation_deposit_payment_cancelled":
                          case "reservation_card_payment_cancelled":
                          case "reservation_deposit_payment_failed":
                            return <MdClose size={24} className="text-cblack3" />;
                          case "c2c_gift_card_payment_received":
                          case "c2c_gift_card_payment_cancelled":
                          case "c2c_gift_card_payment_failed":
                          case "course_wise_payment_paid":
                          case "course_wise_payment_updated":
                          case "course_wise_payment_failed":
                          case "course_wise_payment_cancelled":
                            return <CreditCard size={22} className="text-cblack3" />;
                          default:
                            return <Bell size={22} className="text-cblack3" />;
                        }
                      })()
                    }
                    </div>
                    <div className="flex flex-col">
                      <h2 className={`flex-shrink-0 line-clamp-2 ${item?.is_read ? "text-color4" : "text-cwhite"}`}>
                        <span className="font-medium">{message}</span>
                        {
                          item?.type === "settings_updated" &&  isSettingsOutdated() && (
                            <button className="ml-2 px-2 py-3 text-xs bg-cyellow/10 text-cyellow rounded" title="Refresh the application to apply new settings" onClick={() =>{ setOpen(false); setShowReloadConfirm(true);}}>
                              Refresh Now
                            </button>
                          )
                        }
                      </h2>
                      <span className={`text-xs ${item?.is_read ? "text-color4" : "text-cwhite3"}`}>
                        {timeAgo}
                      </span>
                    </div>
                  </div>
                  {!item?.is_read && <div className="text-xs">🔴</div>}
                </div>
              );
            })}

          {loadingMore &&
            skeletonRows.map((_, index) => (
              <div
                key={`sk-loading-${index}`}
                className="flex items-center gap-4 rounded-md bg-cblack2/60 p-3 animate-pulse"
              >
                <div className="h-10 w-10 rounded-full bg-cblack7/60" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-4/5 rounded bg-cblack7/60" />
                  <div className="h-3 w-2/5 rounded bg-cblack7/60" />
                </div>
              </div>
            ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-2 py-.5 bg-cyellow/10 text-cyellow hover:bg-cyellow/20 rounded border border-cyellow/30 disabled:opacity-50 transition"
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
        
        <div className="mt-6 justify-center items-center">
          <Link to={"/backoffice/notification"}>
            <Button className="w-full bg-cyellow/5 text-cyellow">
              View all notification
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NavNotification;
